import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Plus, Trash2, ArrowLeft, Warehouse } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const EditWarehouseInvoice = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [formData, setFormData] = useState({
    customer_name: '', customer_email: '', customer_phone: '',
    customer_address: '', company_name: '',
    invoice_date: '', payment_terms: 'COD - Cash on Delivery', invoice_type: 'full',
    gst_rate: 0, notes: '', status: 'unpaid',
    quotation_id: null, deposit_percentage: 0, deposit_amount: 0
  });

  const [storageItems, setStorageItems] = useState([]);
  const [handlingFees, setHandlingFees] = useState([]);

  const isPerRate = (unitType) => unitType === 'm3_rate' || unitType === 'sqft_rate' || unitType === 'pallet_rate';

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/warehouse/invoices/${id}`);
        const inv = res.data;
        setFormData({
          customer_name: inv.customer_name || '',
          customer_email: inv.customer_email || '',
          customer_phone: inv.customer_phone || '',
          customer_address: inv.customer_address || '',
          company_name: inv.company_name || '',
          invoice_date: inv.invoice_date ? inv.invoice_date.split('T')[0] : '',
          payment_terms: inv.payment_terms || 'COD - Cash on Delivery',
          invoice_type: inv.invoice_type || 'full',
          gst_rate: inv.gst_rate || 0,
          notes: inv.notes || '',
          status: inv.status || 'unpaid',
          quotation_id: inv.quotation_id || null,
          deposit_percentage: inv.deposit_percentage || 0,
          deposit_amount: inv.deposit_amount || 0
        });
        setStorageItems((inv.storage_items || []).map(item => ({
          description: item.description || 'General Storage',
          unit_type: item.unit_type || 'm3_rate',
          quantity: item.quantity || 0,
          rate_type: item.rate_type || 'per_rate',
          unit_rate: item.unit_rate || 0,
          total_units: item.total_units || 0,
          rate_days: item.rate_days ?? 7,
          amount: item.amount || 0
        })));
        setHandlingFees((inv.handling_fees || []).map(fee => ({
          category: fee.category || 'forklift',
          description: fee.description || '',
          rate_type: fee.rate_type || 'per_usage',
          usage_count: fee.usage_count || 1,
          rate_per_use: fee.rate_per_use || 0,
          amount: fee.amount || 0
        })));
      } catch (error) {
        toast.error('Failed to load invoice');
        navigate('/admin/warehouse');
      } finally {
        setPageLoading(false);
      }
    };
    fetchInvoice();
  }, [id, navigate]);

  const updateStorageItem = (index, field, value) => {
    const updated = [...storageItems];
    updated[index][field] = value;
    if (isPerRate(updated[index].unit_type)) {
      const rate = parseFloat(updated[index].unit_rate) || 0;
      const totalUnits = parseFloat(updated[index].total_units) || 0;
      const parsedDays = parseInt(updated[index].rate_days);
      const days = isNaN(parsedDays) ? 7 : (parsedDays === 0 ? 1 : parsedDays);
      const qty = parseFloat(updated[index].quantity) || 0;
      updated[index].amount = rate * totalUnits * days * qty;
    } else {
      const qty = parseFloat(updated[index].quantity) || 0;
      const rate = parseFloat(updated[index].unit_rate) || 0;
      updated[index].amount = qty * rate;
    }
    setStorageItems(updated);
  };

  const updateHandlingFee = (index, field, value) => {
    const updated = [...handlingFees];
    updated[index][field] = value;
    const count = parseFloat(updated[index].usage_count) || 0;
    const rate = parseFloat(updated[index].rate_per_use) || 0;
    updated[index].amount = count * rate;
    setHandlingFees(updated);
  };

  const calculateTotals = () => {
    const storageTotal = storageItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
    const handlingTotal = handlingFees.reduce((sum, fee) => sum + (parseFloat(fee.amount) || 0), 0);
    const subtotal = storageTotal + handlingTotal;
    const gstAmount = subtotal * (formData.gst_rate / 100);
    const totalAmount = subtotal + gstAmount;
    return { storageTotal, handlingTotal, subtotal, gstAmount, totalAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const totals = calculateTotals();
      const payload = {
        ...formData,
        storage_items: storageItems.map(item => {
          const isRate = isPerRate(item.unit_type);
          const unitLabel = item.unit_type === 'pallet_rate' ? 'Per Pallet' : (item.unit_type === 'm3' || item.unit_type === 'm3_rate' ? 'Per m³' : 'Per Sq Ft');
          const rateLabel = isRate ? ' Rate' : '';
          return {
            description: item.description.startsWith('General Storage') ? `General Storage (${unitLabel}${rateLabel})` : item.description,
            unit_type: item.unit_type,
            quantity: parseFloat(item.quantity) || 0,
            rate_type: isRate ? 'per_rate' : item.rate_type,
            unit_rate: parseFloat(item.unit_rate) || 0,
            total_units: isRate ? parseFloat(item.total_units) || 0 : 0,
            rate_days: isRate ? (isNaN(parseInt(item.rate_days)) ? 7 : parseInt(item.rate_days)) : 0,
            amount: parseFloat(item.amount) || 0
          };
        }),
        handling_fees: handlingFees.map(fee => ({
          category: fee.description.toLowerCase().replace(/\s+/g, '_'),
          description: fee.description,
          rate_type: 'per_usage',
          usage_count: parseFloat(fee.usage_count) || 0,
          rate_per_use: parseFloat(fee.rate_per_use) || 0,
          amount: parseFloat(fee.amount) || 0
        })),
        storage_total: totals.storageTotal,
        handling_total: totals.handlingTotal,
        subtotal: totals.subtotal,
        gst_amount: totals.gstAmount,
        total_amount: totals.totalAmount
      };
      await axios.put(`${BACKEND_URL}/api/warehouse/invoices/${id}`, payload);
      toast.success('Invoice updated successfully');
      navigate(`/admin/warehouse/invoice/${id}`);
    } catch (error) {
      toast.error('Failed to update invoice');
    } finally {
      setLoading(false);
    }
  };

  const totals = calculateTotals();
  const formatCurrency = (amount) => new Intl.NumberFormat('en-SG', { style: 'currency', currency: 'SGD' }).format(amount || 0);

  if (pageLoading) {
    return <div className="min-h-screen pt-44 px-4 flex justify-center items-center"><p>Loading...</p></div>;
  }

  return (
    <div className="min-h-screen pt-44 pb-12 px-4 bg-gray-50">
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-center gap-4 mb-8">
          <Button variant="outline" onClick={() => navigate(`/admin/warehouse/invoice/${id}`)}>
            <ArrowLeft size={16} className="mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
              <Warehouse className="text-green-600" /> Edit Warehouse Invoice
            </h1>
            <p className="text-gray-600">Modify invoice details</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Customer Details */}
          <Card>
            <CardHeader><CardTitle>Customer Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Customer Name *</Label><Input value={formData.customer_name} onChange={(e) => setFormData({...formData, customer_name: e.target.value})} required data-testid="edit-winv-customer-name" /></div>
                <div><Label>Company Name</Label><Input value={formData.company_name} onChange={(e) => setFormData({...formData, company_name: e.target.value})} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Email</Label><Input type="email" value={formData.customer_email} onChange={(e) => setFormData({...formData, customer_email: e.target.value})} /></div>
                <div><Label>Phone</Label><Input value={formData.customer_phone} onChange={(e) => setFormData({...formData, customer_phone: e.target.value})} /></div>
              </div>
              <div><Label>Address</Label><Textarea value={formData.customer_address} onChange={(e) => setFormData({...formData, customer_address: e.target.value})} rows={2} /></div>
              <div className="grid grid-cols-3 gap-4">
                <div><Label>Invoice Date (Job Request Date)</Label><Input type="date" value={formData.invoice_date} onChange={(e) => setFormData({...formData, invoice_date: e.target.value})} data-testid="wh-invoice-date" /></div>
                <div><Label>Payment Terms</Label><Input value={formData.payment_terms} onChange={(e) => setFormData({...formData, payment_terms: e.target.value})} placeholder="COD - Cash on Delivery" data-testid="wh-payment-terms" /></div>
                <div>
                  <Label>Status</Label>
                  <select className="w-full h-10 px-3 text-sm border border-gray-300 rounded-md bg-white" value={formData.status} onChange={(e) => setFormData({...formData, status: e.target.value})}>
                    <option value="unpaid">Unpaid</option>
                    <option value="paid">Paid</option>
                    <option value="overdue">Overdue</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Storage Charges */}
          <Card>
            <CardHeader><div className="flex justify-between items-center"><CardTitle>Storage Charges</CardTitle></div></CardHeader>
            <CardContent className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="font-semibold text-gray-700">Storage Items</h3>
                  <Button type="button" variant="outline" size="sm" onClick={() => setStorageItems([...storageItems, { description: 'General Storage', unit_type: 'm3_rate', quantity: 0, rate_type: 'per_rate', unit_rate: 0, total_units: 0, rate_days: 7, amount: 0 }])} data-testid="add-storage-row-btn">
                    <Plus size={14} className="mr-1" /> Add Row
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="border p-2 text-left w-36">Unit Type</th>
                        <th className="border p-2 text-left w-24">Rate ($)</th>
                        <th className="border p-2 text-left w-24">Total M3/Sqft</th>
                        <th className="border p-2 text-left w-20">Quantity</th>
                        <th className="border p-2 text-left w-24">Rate Type</th>
                        <th className="border p-2 text-right">Amount</th>
                        <th className="border p-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {storageItems.map((item, index) => (
                        <tr key={index}>
                          <td className="border p-1">
                            <select className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md bg-white min-w-[120px]" value={item.unit_type} onChange={(e) => updateStorageItem(index, 'unit_type', e.target.value)}>
                              <option value="m3_rate">per m³</option>
                              <option value="sqft_rate">per sqft</option>
                              <option value="pallet_rate">per pallet</option>
                            </select>
                          </td>
                          <td className="border p-1">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-sm text-gray-500">$</span>
                              <Input type="number" step="0.01" className="h-8 text-sm pl-5 w-20" value={item.unit_rate} onChange={(e) => updateStorageItem(index, 'unit_rate', e.target.value)} />
                            </div>
                          </td>
                          <td className="border p-1"><Input type="number" step="0.01" className="h-8 text-sm w-20" value={item.total_units} onChange={(e) => updateStorageItem(index, 'total_units', e.target.value)} /></td>
                          <td className="border p-1"><Input type="number" step="1" className="h-8 text-sm w-16" value={item.quantity} onChange={(e) => updateStorageItem(index, 'quantity', e.target.value)} /></td>
                          <td className="border p-1">
                            <select className="w-full h-8 px-2 text-sm border border-gray-300 rounded-md bg-white" value={item.rate_days} onChange={(e) => updateStorageItem(index, 'rate_days', e.target.value)}>
                              <option value="7">7 days</option>
                              <option value="30">30 days</option>
                              <option value="0">COD</option>
                            </select>
                          </td>
                          <td className="border p-2 text-right font-semibold">{formatCurrency(item.amount)}</td>
                          <td className="border p-1 text-center">
                            {storageItems.length > 1 && <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setStorageItems(storageItems.filter((_, i) => i !== index))}><Trash2 size={14} /></Button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="text-right border-t pt-4"><p className="text-xl font-bold">Storage Total: {formatCurrency(totals.storageTotal)}</p></div>
            </CardContent>
          </Card>

          {/* Handling Fees */}
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Handling Fees</CardTitle>
                <Button type="button" variant="outline" onClick={() => setHandlingFees([...handlingFees, { category: '', description: '', rate_type: 'per_usage', usage_count: 1, rate_per_use: 0, amount: 0 }])} data-testid="add-handling-fee-btn">
                  <Plus size={16} className="mr-2" /> Add Fee
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {handlingFees.map((fee, index) => (
                <div key={index} className="border p-4 rounded-lg space-y-4">
                  <div className="flex justify-between items-start">
                    <p className="font-semibold">Handling Fee {index + 1}</p>
                    {handlingFees.length > 1 && <Button type="button" variant="outline" size="sm" onClick={() => setHandlingFees(handlingFees.filter((_, i) => i !== index))}><Trash2 size={16} /></Button>}
                  </div>
                  <div>
                    <Label>Equipment Category</Label>
                    <Input list="handling-categories" placeholder="e.g. Forklift, Pallet Jack..." value={fee.description} onChange={(e) => updateHandlingFee(index, 'description', e.target.value)} />
                    <datalist id="handling-categories">
                      <option value="Forklift" />
                      <option value="Pallet Jack" />
                      <option value="Manpower" />
                      <option value="Shrink Wrap" />
                    </datalist>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div><Label>Rate Per Usage ($)</Label><Input type="number" step="0.01" value={fee.rate_per_use} onChange={(e) => updateHandlingFee(index, 'rate_per_use', e.target.value)} /></div>
                    <div><Label>Quantity</Label><Input type="number" step="1" min="1" value={fee.usage_count} onChange={(e) => updateHandlingFee(index, 'usage_count', e.target.value)} /></div>
                    <div><Label>Amount</Label><div className="h-10 px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 flex items-center font-semibold">{formatCurrency(fee.amount)}</div></div>
                  </div>
                </div>
              ))}
              <div className="text-right border-t pt-4"><p className="text-xl font-bold">Handling Total: {formatCurrency(totals.handlingTotal)}</p></div>
            </CardContent>
          </Card>

          {/* GST & Notes */}
          <Card>
            <CardHeader><CardTitle>Additional Details</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>GST Rate (%)</Label><Input type="number" step="0.1" value={formData.gst_rate} onChange={(e) => setFormData({...formData, gst_rate: parseFloat(e.target.value) || 0})} /></div>
              </div>
              <div><Label>Notes / Remarks</Label><Textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows={3} /></div>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card>
            <CardHeader><CardTitle>Summary</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="flex justify-between"><span>Storage Charges</span><span>{formatCurrency(totals.storageTotal)}</span></div>
                <div className="flex justify-between"><span>Handling Fees</span><span>{formatCurrency(totals.handlingTotal)}</span></div>
                <div className="flex justify-between font-semibold border-t pt-2"><span>Subtotal</span><span>{formatCurrency(totals.subtotal)}</span></div>
                {formData.gst_rate > 0 && <div className="flex justify-between"><span>GST ({formData.gst_rate}%)</span><span>{formatCurrency(totals.gstAmount)}</span></div>}
                <div className="flex justify-between text-xl font-bold border-t pt-2 text-green-600"><span>Total Amount</span><span>{formatCurrency(totals.totalAmount)}</span></div>
              </div>
            </CardContent>
          </Card>

          <Button data-testid="update-warehouse-invoice-submit" type="submit" className="w-full bg-green-600 hover:bg-green-700" disabled={loading}>
            {loading ? 'Updating...' : 'Update Invoice'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default EditWarehouseInvoice;
